import { useState } from 'react';
import { algebra2TestNY } from './data/algebra2TestNY';
import { algebra2TestOH } from './data/algebra2TestOH';
import { algebra2TestTX } from './data/algebra2TestTX';
import { algebra2TestVA } from './data/algebra2TestVA';
import { algebra2TestPA } from './data/algebra2TestPA';
import Algebra2TestCard from './components/Algebra2TestCard';

const tests = [
  {
    id: 'ny',
    state: '🗽',
    stateName: 'New York',
    fullName: 'New York Regents',
    description: 'Comprehensive Algebra 2 & Trigonometry assessment covering polynomials, complex numbers, exponential/logarithmic functions, trigonometry, and statistics.',
    questions: algebra2TestNY,
    color: 'bg-gradient-to-br from-indigo-600 to-purple-600'
  },
  {
    id: 'oh',
    state: '🌻',
    stateName: 'Ohio',
    fullName: 'Ohio State Assessment',
    description: 'Focus on exponential functions, sequences, probability, and algebraic reasoning with real-world applications.',
    questions: algebra2TestOH,
    color: 'bg-gradient-to-br from-red-600 to-orange-600'
  },
  {
    id: 'tx',
    state: '⭐',
    stateName: 'Texas',
    fullName: 'Texas STAAR',
    description: 'Emphasis on conic sections, rational functions, complex numbers, and advanced algebraic problem-solving.',
    questions: algebra2TestTX,
    color: 'bg-gradient-to-br from-blue-600 to-cyan-600'
  },
  {
    id: 'va',
    state: '🏛️',
    stateName: 'Virginia',
    fullName: 'Virginia SOL',
    description: 'Comprehensive coverage of matrices, complex numbers, data analysis, and statistical reasoning.',
    questions: algebra2TestVA,
    color: 'bg-gradient-to-br from-green-600 to-teal-600'
  },
  {
    id: 'pa',
    state: '🔔',
    stateName: 'Pennsylvania',
    fullName: 'Pennsylvania Keystone',
    description: 'Focus on binomial theorem, exponential models, polynomial operations, and advanced algebraic techniques.',
    questions: algebra2TestPA,
    color: 'bg-gradient-to-br from-yellow-600 to-amber-600'
  }
];

export default function Algebra2TestsPage() {
  const [selectedTest, setSelectedTest] = useState<string | null>(null);

  if (selectedTest) {
    const test = tests.find(t => t.id === selectedTest);
    if (!test) return null;

    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-5xl mx-auto mb-8">
          <button
            onClick={() => setSelectedTest(null)}
            className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-medium mb-6 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Test Selection
          </button>
        </div>
        <Algebra2TestCard
          state={test.state}
          stateName={test.stateName}
          questions={test.questions}
          color={test.color}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 text-white py-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl font-bold mb-6">Algebra 2 State Practice Tests</h1>
          <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
            Master Algebra 2 & Trigonometry with authentic state assessments from across the nation. 
            Each test features 30 comprehensive questions with detailed step-by-step solutions.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-lg">
            <div className="flex items-center gap-2">
              <i className="ri-checkbox-circle-fill text-2xl"></i>
              <span>150 Total Questions</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-book-open-fill text-2xl"></i>
              <span>Complete Solutions</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-bar-chart-fill text-2xl"></i>
              <span>Instant Scoring</span>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Banner - NY Regents Open Exam */}
      <div className="max-w-6xl mx-auto px-4 -mt-12 mb-12">
        <a
          href="/grades/algebra2-regents-open"
          className="block bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 rounded-2xl p-8 shadow-2xl hover:shadow-3xl transition-all hover:scale-[1.02] cursor-pointer"
        >
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-4xl">🗽</span>
                <h3 className="text-2xl font-bold text-white">NY Algebra 2 & Trig Regents — Full Open-Ended Exam</h3>
              </div>
              <p className="text-white/90 text-lg mb-4">
                Complete authentic 4-part NY Regents exam with Part I multiple-choice, Parts II-IV open-ended questions, 
                sample answers, scoring rubrics, and detailed explanations. 86 points total.
              </p>
              <div className="flex flex-wrap gap-4 text-sm text-white/80">
                <span className="flex items-center gap-1">
                  <i className="ri-file-list-3-line"></i>
                  Part I: 24 MCQ (48 pts)
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-edit-line"></i>
                  Part II: 8 Short Answer (16 pts)
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-article-line"></i>
                  Part III: 4 Extended (16 pts)
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-file-text-line"></i>
                  Part IV: 1 Comprehensive (6 pts)
                </span>
              </div>
            </div>
            <div className="ml-6">
              <div className="bg-white/20 backdrop-blur-sm rounded-full w-16 h-16 flex items-center justify-center">
                <i className="ri-arrow-right-line text-3xl text-white"></i>
              </div>
            </div>
          </div>
        </a>
      </div>

      {/* Teacher Message */}
      <div className="max-w-6xl mx-auto px-4 mb-12">
        <div className="bg-white rounded-xl shadow-sm p-8 border-l-4 border-indigo-600">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-user-smile-line text-2xl text-indigo-600"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Message from Mr. Augustine</h3>
              <p className="text-gray-700 leading-relaxed">
                These state practice tests represent the culmination of your Algebra 2 & Trigonometry journey. Each assessment 
                is carefully designed to mirror actual state exams, covering everything from polynomial operations and complex 
                numbers to trigonometric identities and statistical analysis. Take your time with each question, show your work, 
                and review the detailed solutions to understand not just what the answer is, but why it's correct. Remember: 
                mastery comes from understanding the process, not just memorizing formulas. You've got this!
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Test Cards */}
      <div className="max-w-6xl mx-auto px-4 pb-16">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Choose Your State Test</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {tests.map((test) => (
            <button
              key={test.id}
              onClick={() => setSelectedTest(test.id)}
              className="text-left bg-white rounded-xl shadow-sm hover:shadow-xl transition-all p-6 border-2 border-gray-100 hover:border-indigo-300 group cursor-pointer"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-4xl">{test.state}</span>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 group-hover:text-indigo-600 transition-colors">
                      {test.stateName}
                    </h3>
                    <p className="text-sm text-gray-600">{test.fullName}</p>
                  </div>
                </div>
                <div className="w-10 h-10 bg-gray-100 group-hover:bg-indigo-100 rounded-full flex items-center justify-center transition-colors">
                  <i className="ri-arrow-right-line text-xl text-gray-600 group-hover:text-indigo-600"></i>
                </div>
              </div>
              <p className="text-gray-700 mb-4 leading-relaxed">{test.description}</p>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <i className="ri-file-list-line"></i>
                  30 Questions
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-star-line"></i>
                  60 Points
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  ~90 min
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Standards Coverage */}
      <div className="bg-white py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Comprehensive Standards Coverage</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6">
              <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-function-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Polynomial & Rational Functions</h3>
              <p className="text-gray-700 text-sm">
                Operations, factoring, division, graphing, end behavior, asymptotes, and complex zeros
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6">
              <div className="w-12 h-12 bg-purple-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-compass-3-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Trigonometry</h3>
              <p className="text-gray-700 text-sm">
                Unit circle, trig functions, identities, equations, inverse functions, law of sines & cosines
              </p>
            </div>
            <div className="bg-gradient-to-br from-pink-50 to-red-50 rounded-xl p-6">
              <div className="w-12 h-12 bg-pink-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-line-chart-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Exponential & Logarithmic</h3>
              <p className="text-gray-700 text-sm">
                Growth/decay models, logarithm properties, solving equations, natural logarithms
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6">
              <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-numbers-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Complex Numbers</h3>
              <p className="text-gray-700 text-sm">
                Operations, conjugates, absolute value, powers of i, quadratic equations with complex solutions
              </p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6">
              <div className="w-12 h-12 bg-green-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shape-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Conic Sections</h3>
              <p className="text-gray-700 text-sm">
                Circles, parabolas, ellipses, hyperbolas — equations, graphing, and applications
              </p>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-6">
              <div className="w-12 h-12 bg-yellow-600 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-bar-chart-box-line text-2xl text-white"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Statistics & Probability</h3>
              <p className="text-gray-700 text-sm">
                Data analysis, measures of center/spread, normal distribution, binomial theorem, matrices
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tips Section */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-2xl p-8 text-white">
          <h2 className="text-3xl font-bold mb-6 text-center">Test-Taking Strategies</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="flex items-start gap-3">
                <i className="ri-lightbulb-line text-2xl mt-1"></i>
                <div>
                  <h3 className="font-bold text-lg mb-2">Read Carefully</h3>
                  <p className="text-white/90 text-sm">
                    Pay attention to keywords like "simplify," "solve," "factor," or "evaluate." Each requires a different approach.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="flex items-start gap-3">
                <i className="ri-calculator-line text-2xl mt-1"></i>
                <div>
                  <h3 className="font-bold text-lg mb-2">Show Your Work</h3>
                  <p className="text-white/90 text-sm">
                    Even on multiple-choice, work through problems step-by-step. This helps catch errors and builds understanding.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-line text-2xl mt-1"></i>
                <div>
                  <h3 className="font-bold text-lg mb-2">Check Your Answers</h3>
                  <p className="text-white/90 text-sm">
                    Substitute solutions back into original equations. Verify that denominators aren't zero in rational expressions.
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="flex items-start gap-3">
                <i className="ri-time-line text-2xl mt-1"></i>
                <div>
                  <h3 className="font-bold text-lg mb-2">Manage Your Time</h3>
                  <p className="text-white/90 text-sm">
                    Spend about 2-3 minutes per question. If stuck, mark it and return later with fresh perspective.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}